import {useCallback} from "react";
import {FieldPath, FieldValues, PathValue, UseFormReturn, useWatch} from "react-hook-form";
import {Image} from "@/images/Image.ts";
import {useTemporaryImageUpload} from "@/images/useTemporaryImageUpload.ts";
import {notifyError} from "@/api/ErrorNotification.ts";

type Options<
    TFieldValues extends FieldValues,
    TTransformedValues extends FieldValues | undefined,
    TName extends FieldPath<TFieldValues>,
> = {
    form: UseFormReturn<TFieldValues, unknown, TTransformedValues>;
    name: TName;
    onUploadStart?: () => void;
    onError?: (reason: unknown) => void;
};

export function useTemporaryImageFormField<
    TFieldValues extends FieldValues,
    TTransformedValues extends FieldValues | undefined,
    TName extends FieldPath<TFieldValues>,
>({form, name, onUploadStart, onError}: Options<TFieldValues, TTransformedValues, TName>) {
    const {upload, commit, clear, uploading} = useTemporaryImageUpload();
    const image = useWatch({control: form.control, name}) as Image | undefined;

    const onFileSelected = useCallback(async (file: File) => {
        onUploadStart?.();
        try {
            const uploadedImage = await upload(file);
            if (uploadedImage) {
                form.setValue(name, uploadedImage as PathValue<TFieldValues, TName>, {
                    shouldDirty: true,
                    shouldValidate: true,
                });
            }
        } catch (reason) {
            notifyError(reason, "Das Bild konnte nicht hochgeladen werden.");
            onError?.(reason);
        }
    }, [form, name, onError, onUploadStart, upload]);

    const clearImage = useCallback(() => {
        clear();
        form.setValue(name, null as PathValue<TFieldValues, TName>, {
            shouldDirty: true,
            shouldValidate: true,
        });
    }, [clear, form, name]);

    return {
        image,
        uploading,
        onFileSelected,
        clearImage,
        commit,
    };
}
